import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ISyncTransactionActionLog,
  ISyncTransactionActionWithLog,
} from "../../../../models/sync-transaction-action-with-log";
import { useFetch } from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import { getSyncTransactionActionWithLog } from "../../../../services/syncTransactionActionWithLog.service";
import { ISyncTransaction } from "../../../../models/sync-transaction";
import useRestartSyncTransactions, {
  isSyncTransactionError,
} from "./useRestartSyncTransactions";

const translationPath = "page.4uBus.logs.syncTransaction.";

interface IProps {
  syncTransaction: ISyncTransaction;
  onClose: () => void;
  onRestarted?: () => void;
}

const SyncTransactionActionsModal: React.FC<IProps> = ({
  syncTransaction,
  onClose,
  onRestarted,
}) => {
  const { t, i18n } = useTranslation();

  const {
    canRestart,
    restart,
    loading: restarting,
  } = useRestartSyncTransactions();

  const handleRestart = async () => {
    const { executed } = await restart([Number(syncTransaction.identifier)]);

    if (executed) onRestarted?.();
  };

  const { sendRequest, loading } = useFetch(getSyncTransactionActionWithLog);

  const [data, setData] = useState<ISyncTransactionActionWithLog | null>(null);

  const [pageNumber, setPageNumber] = useState(1);

  const formatDate = (value?: string | null) => {
    if (!value) return "-";

    return format(new Date(value), "Pp", {
      locale:
        i18n.language === "pt-BR"
          ? ptBR
          : i18n.language === "en-US"
            ? enUS
            : es,
    }).replaceAll(",", "");
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      const { data, success } = await sendRequest({
        identifier: syncTransaction.identifier,
        pageNumber: pageNumber,
        pageSize: 10,
      });
      if (data && success) {
        setData(data);
      }
    };

    fetchTransactions();
  }, [sendRequest, pageNumber, syncTransaction.identifier]);

  const renderValue = (label: string, value?: string | number | null) => (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ lineHeight: 1.4, wordBreak: "break-word" }}
      >
        {value ?? "-"}
      </Typography>
    </Box>
  );

  const renderLog = (log: ISyncTransactionActionLog, index: number) => (
    <Paper key={log.identifier ?? index} variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
          alignItems: "end",
          mb: 2,
        }}
      >
        {renderValue(t(translationPath + "identifier"), log.identifier)}
        <Box>
          <Chip
            size="small"
            label={
              log.success === 1
                ? t(translationPath + "status.success")
                : t(translationPath + "status.error")
            }
            color={log.success === 1 ? "success" : "error"}
          />
        </Box>
        {renderValue(t(translationPath + "created"), formatDate(log.created))}
        {renderValue(
          t(translationPath + "last_modified"),
          formatDate(log.lastmodified),
        )}
      </Box>
      <Typography
        component="pre"
        sx={{
          boxSizing: "border-box",
          m: 0,
          p: 1.5,
          borderRadius: 1,
          backgroundColor: (theme) => theme.palette.grey[100],
          fontFamily: "monospace",
          fontSize: "0.8rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          width: "100%",
          maxWidth: "100%",
          maxHeight: 240,
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        {log.data || "-"}
      </Typography>
    </Paper>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogActions>
        {canRestart && isSyncTransactionError(syncTransaction.statusCode) && (
          <Button
            startIcon={<ReplayIcon fontSize="small" />}
            onClick={handleRestart}
            disabled={restarting}
          >
            {t(translationPath + "resend_row")}
          </Button>
        )}
        <Button onClick={onClose}>X</Button>
      </DialogActions>
      <DialogTitle sx={{ paddingY: 0, paddingX: 6, overflow: "hidden" }}>
        {t(translationPath + "actions")} - {syncTransaction.name}
      </DialogTitle>
      <DialogContent
        sx={{ height: 500, width: "100%", marginTop: 0, overflow: "auto" }}
      >
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
              columnGap: 4,
              rowGap: 2,
            }}
          >
            {renderValue(t(translationPath + "name"), syncTransaction.name)}
            {renderValue(t(translationPath + "cpf"), syncTransaction.document)}
            {renderValue(
              t(translationPath + "external_id"),
              syncTransaction.externalId,
            )}
            {renderValue(
              t(translationPath + "last_modified"),
              formatDate(syncTransaction.lastModified),
            )}
          </Box>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : !data?.items.length ? (
          <Typography color="text.secondary">
            {t(translationPath + "no_transactions")}
          </Typography>
        ) : (
          <Stack gap={1}>
            {data.items.map(({ actionLogs: logs, ...action }) => (
              <Accordion key={action.identifier}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    columnGap={3}
                    rowGap={1}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ width: "100%", pr: 1 }}
                  >
                    <Typography fontWeight={600}>
                      #{action.identifier}
                    </Typography>
                    <Typography>{action.softwareName}</Typography>
                    <Chip size="small" label={action.statusCode || "-"} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(action.created)}
                    </Typography>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(3, minmax(0, 1fr))",
                      },
                      columnGap: 4,
                      rowGap: 2.5,
                      mb: 2,
                    }}
                  >
                    {renderValue(
                      t(translationPath + "original_value"),
                      action.originalValue,
                    )}
                    {renderValue(
                      t(translationPath + "new_value"),
                      action.newValue,
                    )}
                    {renderValue(
                      t(translationPath + "message"),
                      action.statusMessage,
                    )}
                    {renderValue(
                      t(translationPath + "retries"),
                      action.retries,
                    )}
                    {renderValue(
                      t(translationPath + "created"),
                      formatDate(action.created),
                    )}
                    {renderValue(
                      t(translationPath + "last_modified"),
                      formatDate(action.lastmodified),
                    )}
                  </Box>

                  <Typography variant="subtitle1" mb={1}>
                    {t(translationPath + "logs")}
                  </Typography>

                  <Stack gap={1}>
                    {logs.length ? (
                      logs.map(renderLog)
                    ) : (
                      <Typography color="text.secondary">
                        {t(translationPath + "no_logs")}
                      </Typography>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </DialogContent>
      {!!data?.totalPages && data.totalPages > 1 && (
        <DialogActions sx={{ justifyContent: "center" }}>
          <Pagination
            count={data.totalPages}
            page={pageNumber}
            onChange={(_, page) => setPageNumber(page)}
            disabled={loading}
          />
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SyncTransactionActionsModal;
